import { LocusChromosome, ZygosityLabel } from './enums.mjs';
function wildTypeSymbol(locus) {
    return locus.alleles.find((a) => a.isWildType)?.symbol ?? locus.alleles[0]?.symbol ?? '';
}
/** Map allele symbol to numeric intensity (dominanceRank, or 1 for non-wild). */
export function alleleIntensityScore(locus, symbol) {
    const allele = locus.alleles.find((a) => a.symbol === symbol);
    if (!allele || allele.isWildType)
        return 0;
    return allele.dominanceRank ?? 1;
}
export function maxPolygenicScore(locus) {
    const scores = locus.alleles.map((a) => a.isWildType ? 0 : (a.dominanceRank ?? 1));
    return Math.max(...scores, 1);
}
/** Mid-parent average intensity for a bird at a polygenic locus. */
export function parentPolygenicScore(locus, alleles, sex) {
    const wt = wildTypeSymbol(locus);
    if (locus.chromosome === LocusChromosome.Z && sex === 'FEMALE') {
        return alleleIntensityScore(locus, alleles[0] ?? wt);
    }
    const a1 = alleleIntensityScore(locus, alleles[0] ?? wt);
    const a2 = alleleIntensityScore(locus, alleles[1] ?? alleles[0] ?? wt);
    return (a1 + a2) / 2;
}
export function polygenicIntensityLabel(locus, score) {
    const max = maxPolygenicScore(locus);
    if (score <= 0)
        return 'Wild type';
    const ratio = score / max;
    if (ratio < 0.34)
        return `Low ${locus.name}`;
    if (ratio < 0.67)
        return `Moderate ${locus.name}`;
    return `High ${locus.name}`;
}
/**
 * Predict offspring intensity buckets from mid-parent score.
 * Uses a simple regression-to-mean model (heritability h = 0.5).
 */
export function predictPolygenicOffspring(locus, male, female) {
    const max = maxPolygenicScore(locus);
    const maleScore = parentPolygenicScore(locus, male.alleles, 'MALE');
    const femaleScore = parentPolygenicScore(locus, female.alleles, 'FEMALE');
    const midParent = (maleScore + femaleScore) / 2;
    const populationMean = max * 0.25;
    const heritability = 0.5;
    const expected = populationMean + heritability * (midParent - populationMean);
    const lowPhenotype = `Low ${locus.name}`;
    const modPhenotype = `Moderate ${locus.name}`;
    const highPhenotype = `High ${locus.name}`;
    const relative = Math.min(1, Math.max(0, expected / max));
    const low = Math.max(0.12, 0.55 - relative * 0.4);
    const high = Math.max(0.12, 0.55 - (1 - relative) * 0.4);
    const moderate = Math.max(0.1, 1 - low - high);
    const total = low + moderate + high;
    const mk = (phenotype, probability) => ({
        locusSymbol: locus.symbol,
        genotype: 'quantitative',
        phenotype,
        zygosity: ZygosityLabel.VISUAL,
        probability: probability / total,
        sex: 'EITHER',
        isVisual: phenotype !== 'Wild type',
        isSplit: false,
    });
    return [mk(lowPhenotype, low), mk(modPhenotype, moderate), mk(highPhenotype, high)].sort((a, b) => b.probability - a.probability);
}
